/**
 * V1 Survey Status Card
 * 
 * Displays survey status messages in a consistent card format.
 * Used for errors, already taken surveys, and other status messages.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Lock, AlertCircle } from 'lucide-react';

interface V1SurveyStatusCardProps {
  title: string;
  message: string;
  status: 'success' | 'error' | 'warning' | 'info';
  eventSlug: string;
  showSurveysButton?: boolean;
  showScheduleButton?: boolean;
  isSessionSurvey?: boolean;
}

export const V1SurveyStatusCard: React.FC<V1SurveyStatusCardProps> = ({
  title,
  message,
  status,
  eventSlug,
  showSurveysButton = true,
  showScheduleButton = true,
  isSessionSurvey = false,
}) => {
  const navigate = useNavigate();

  // Determine icon and colors based on status
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: CheckCircle,
          iconColor: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
        };
      case 'error':
        return {
          icon: XCircle,
          iconColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
        };
      case 'warning':
        return {
          icon: AlertCircle,
          iconColor: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
        };
      case 'info':
      default:
        return {
          icon: AlertCircle,
          iconColor: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
        };
    }
  };

  const { icon: Icon, iconColor, bgColor, borderColor } = getStatusConfig();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className={`max-w-md w-full ${bgColor} ${borderColor} border rounded-lg p-8 text-center`}>
        <div className={`inline-flex items-center justify-center w-16 h-16 ${bgColor} rounded-full mb-4`}>
          <Icon className={`w-8 h-8 ${iconColor}`} />
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-6">{message}</p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showSurveysButton && (
            <button
              onClick={() => navigate(`/surveys`)}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to surveys
            </button>
          )}
          
          {showScheduleButton && isSessionSurvey && (
            <button
              onClick={() => navigate(`/schedule`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to schedule
            </button>
          )}
          
          {showScheduleButton && !isSessionSurvey && !showSurveysButton && (
            <button
              onClick={() => navigate(`/${eventSlug}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to event
            </button>
          )}
        </div>
      </div>
    </div>
  );
};